import { Icon } from '../ui/Icon';

interface Testimonial {
  id: number;
  text: string;
  author: string;
  role: string;
  location: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    text: "The efficiency and transparency EasyBuy brings to the table is unmatched. They handled our entire fleet procurement with zero hiccups.",
    author: "Fatima Khoury",
    role: "Logistics Manager",
    location: "UAE"
  },
  {
    id: 2,
    text: "Sourcing premium spices from India was always a challenge until we partnered with EasyBuy. The quality consistency is remarkable.",
    author: "Hassan Ali",
    role: "Procurement Director",
    location: "Saudi Arabia"
  },
  {
    id: 3,
    text: "Their industrial machinery support helped us set up our new automotive plant in record time. A truly global partner.",
    author: "Jorge Martínez",
    role: "Operations Head",
    location: "Spain"
  },
  {
    id: 4,
    text: "From camping gear to heavy equipment, their diverse portfolio covers all our retail needs. Highly recommended.",
    author: "Sarah Jenkins",
    role: "Retail Chain Owner",
    location: "Kenya"
  },
];

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => (
  <div className="min-w-[300px] md:min-w-[400px] bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group">
    <div className="flex gap-1 mb-6">
      {[1, 2, 3, 4, 5].map((star) => (
        <Icon key={star} name="star" className="w-5 h-5 text-orange-400 fill-orange-400" />
      ))}
    </div>
    <p className="text-gray-600 text-lg leading-relaxed mb-8">
      "{testimonial.text}"
    </p>
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center text-orange-600 font-bold text-xl">
        {testimonial.author.charAt(0)}
      </div>
      <div>
        <div className="font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
          {testimonial.author}
        </div>
        <div className="text-sm text-gray-400 flex items-center gap-2">
          <span>{testimonial.role}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{testimonial.location}</span>
        </div>
      </div>
    </div>
  </div>
)

export default function Testimonials() {
  return (
    <section className="py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-16 md:flex justify-between items-end">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-200 bg-orange-50 mb-6">
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            <span className="text-xs font-semibold text-orange-600 tracking-wider">CLIENT SUCCESS STORIES</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
            Trusted by Global <span className="text-orange-500">Industry Leaders</span>
          </h2>
        </div>

        <div className="hidden md:flex gap-2">
          <button className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-white hover:shadow-lg transition-all">
            <Icon name="arrow-left" className="w-5 h-5 text-gray-600" />
          </button>
          <button className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30 hover:bg-orange-600 transition-all">
            <Icon name="arrow-right" className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Scrolling Container */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />

        <div className="flex gap-6 overflow-x-auto pb-8 pt-4 px-6 md:px-0 scrollbar-hide snap-x">
          {/* Spacer for left padding on desktop */}
          <div className="w-0 md:w-[max(0px,calc(50vw-40rem))]" />

          {testimonials.map((t) => (
            <TestimonialCard key={t.id} testimonial={t} />
          ))}
          {testimonials.map((t) => (
            <TestimonialCard key={`dup-${t.id}`} testimonial={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
